import { FC } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { buildCoursePath } from '@new/common/lib/buildPaths';
import { GraduationCap } from 'lucide-react';
import { CourseActionButton } from '@new/features/courses/components/CourseActionButton/CourseActionButton';
import { CourseWithProgress } from '@teachur/http-client';
import { Can } from '@new/casl';
import { StepIconProgress } from './StepIconProgress';

interface CourseCardProps {
  course: CourseWithProgress;
  degreeId?: number;
  showMoreMenu?: boolean;
  readOnly?: boolean;
}

export const CourseCard: FC<CourseCardProps> = ({ course, degreeId, readOnly }) => {
  const navigate = useNavigate();
  const status = course.status ?? 'notStarted';
  const isPaused = !!course.isPaused;
  const hasImage = !!course.image?.url;

  const handleCardClick = () => {
    navigate({ to: buildCoursePath({ degreeId, courseId: course.id }) });
  };

  return (
    <div className="relative flex flex-col md:flex-row justify-between items-start p-4 md:p-5 bg-(--neutral-white-NW1000)! rounded-xl border-2 border-(--neutral-black-NB100) shadow-sm hover:shadow-md transition-shadow w-full gap-6">
      <div
        onClick={handleCardClick}
        className="flex flex-col md:flex-row items-start gap-4 md:gap-8 cursor-pointer flex-1 w-full"
      >
        <div className="relative w-full md:w-56 shrink-0">
          {hasImage ? (
            <img
              src={course.image!.url}
              alt={course.name}
              className="w-full md:w-56 h-45 md:h-52.25 object-cover rounded-[12px]"
            />
          ) : (
            <GraduationCap className="w-full sm:w-56 h-45 sm:h-52.25 object-cover rounded-[12px] text-gray-400" />
          )}
        </div>

        <div className="flex flex-col gap-8 flex-1 w-full min-w-0">
          <div className="flex flex-col gap-4">
            <p className="[font-size:var(--fs-p3)]! md:[font-size:var(--fs-p2)]! text-(--neutral-black-NB1100)! font-semibold leading-tight m-0!">
              {course.name}
            </p>

            <p className="[font-size:var(--fs-p4)]! md:[font-size:var(--fs-p3)]! text-(--neutral-black-NB800)! m-0! leading-tight line-clamp-5 md:line-clamp-none max-w-full md:max-w-md lg:max-w-4xl">
              {course.shortDescription}
            </p>
          </div>

          <Can I="start" a="Course">
            <StepIconProgress course={course} />
          </Can>
        </div>
      </div>
      <Can I="start" a="Course" context={{ readOnly }}>
        <CourseActionButton
          courseId={course.id}
          status={status}
          isPaused={isPaused}
          degreeId={degreeId}
          readOnly={readOnly}
        />
      </Can>
    </div>
  );
};
